import React, { useState } from 'react'
import { Button, TextField, TextArea, Banner } from '../../ui'
import { appsyncQuery } from '../../api/appsync-client.js'

const PUBLISH_NOTIFICATION = `
  mutation PublishNotification($input: PublishNotificationInput!) {
    publishNotification(input: $input) {
      id type title body targetOrg targetRoles createdAt
    }
  }
`

/**
 * Inline notification compose form.
 *
 * For managers: audience is "All my reports" or an individual report.
 * For admins: audience is "All users", "All reports", or "All managers".
 */
export function SendNotificationForm({ me, reports = [], isAdmin = false, onSent, onCancel }) {
  const [audience, setAudience] = useState('all')
  const [title, setTitle] = useState('')
  const [body, setBody] = useState('')
  const [type, setType] = useState('info')
  const [sending, setSending] = useState(false)
  const [error, setError] = useState(null)
  const [success, setSuccess] = useState(false)

  const audienceOptions = isAdmin
    ? [
        { value: 'all', label: 'All users' },
        { value: 'reports', label: 'All reports' },
        { value: 'managers', label: 'All managers' },
      ]
    : [
        { value: 'all', label: 'All my reports' },
        ...reports.map((r) => ({ value: r.reportId, label: r.reportName || r.reportEmail })),
      ]

  async function handleSend(e) {
    e.preventDefault()
    if (!title.trim() || !body.trim()) return

    setSending(true)
    setError(null)
    setSuccess(false)

    try {
      const input = {
        targetOrg: me.org,
        type,
        title: title.trim(),
        body: body.trim(),
        targetRoles: [],
      }

      if (isAdmin) {
        if (audience === 'all') input.targetRoles = ['owner', 'admin', 'manager', 'report']
        else if (audience === 'reports') input.targetRoles = ['report']
        else if (audience === 'managers') input.targetRoles = ['manager']
      } else {
        // Manager sending
        if (audience === 'all') {
          input.targetRoles = ['report']
          input.targetManagerId = me.userId
        } else {
          // Individual report
          input.targetUserId = audience
          input.targetRoles = ['report']
        }
      }

      await appsyncQuery(PUBLISH_NOTIFICATION, { input })
      setSuccess(true)
      setTitle('')
      setBody('')
      setTimeout(() => { setSuccess(false); onSent?.() }, 2000)
    } catch (err) {
      setError(err?.message || 'Failed to send notification.')
    } finally {
      setSending(false)
    }
  }

  return (
    <form onSubmit={handleSend} style={styles.form}>
      {error && <Banner tone="error">{error}</Banner>}
      {success && <Banner tone="success">Notification sent</Banner>}

      <div style={styles.field}>
        <label style={styles.label}>To</label>
        <select
          style={styles.select}
          value={audience}
          onChange={(e) => setAudience(e.target.value)}
        >
          {audienceOptions.map((opt) => (
            <option key={opt.value} value={opt.value}>{opt.label}</option>
          ))}
        </select>
      </div>

      <TextField
        label="Title"
        value={title}
        onChange={(e) => setTitle(e.target.value)}
        placeholder="Notification title"
        required
      />

      <TextArea
        label="Message"
        value={body}
        onChange={(e) => setBody(e.target.value)}
        placeholder="Write your message..."
        rows={3}
        required
      />

      <div style={styles.actions}>
        {onCancel && (
          <Button variant="ghost" size="sm" type="button" onClick={onCancel}>
            Cancel
          </Button>
        )}
        <Button
          type="submit"
          size="sm"
          loading={sending}
          loadingLabel="Sending..."
          disabled={!title.trim() || !body.trim()}
        >
          Send
        </Button>
      </div>
    </form>
  )
}

const styles = {
  form: { display: 'flex', flexDirection: 'column', gap: '10px' },
  field: { display: 'flex', flexDirection: 'column', gap: '4px' },
  label: { fontSize: '12px', fontWeight: 500, color: 'var(--text-secondary)' },
  select: {
    padding: '6px 8px',
    borderRadius: '6px',
    border: '1px solid var(--border)',
    background: 'var(--surface)',
    color: 'var(--text-primary)',
    fontSize: '13px',
    fontFamily: 'inherit',
  },
  actions: { display: 'flex', justifyContent: 'flex-end', gap: '8px' },
}
